import { useRef } from 'react';
import { DOCUMENT_MAX_BYTES, formatBytes } from '@kontor/shared';
import { apiUpload, queryString } from '../../api';
import { useSession } from '../../session';

/** Beleg zu einem Fahrzeugeintrag (Tanken, Kosten, Wartung): Datei, Lieferant, Rechnungsnummer und ob schon bezahlt */
export interface ReceiptDraft {
  file: File | null;
  supplier: string;
  number: string;
  paid: '' | 'card' | 'cash' | 'bank_transfer' | 'paypal';
}

export const emptyReceipt = (supplier = ''): ReceiptDraft => ({ file: null, supplier, number: '', paid: 'card' });

/** Prüft die Eingaben, bevor der Eintrag gespeichert wird (Fehlertext oder null) */
export function receiptProblem(r: ReceiptDraft): string | null {
  if (!r.file) return null;
  if (r.file.size > DOCUMENT_MAX_BYTES) return `${r.file.name} ist zu groß, erlaubt sind ${formatBytes(DOCUMENT_MAX_BYTES)}.`;
  if (!r.supplier.trim()) return 'Bitte nennen Sie den Lieferanten des Belegs, zum Beispiel die Tankstelle oder die Werkstatt.';
  return null;
}

export interface ReceiptResult {
  invoiceId: string;
  number: string;
  status: 'draft' | 'approved';
  paid: boolean;
  notes: string[];
}

/** Lädt den Beleg hoch. Aus dem gespeicherten Eintrag entsteht damit eine Eingangsrechnung. */
export async function uploadReceipt(kind: 'costs' | 'maintenance', entryId: string, r: ReceiptDraft): Promise<ReceiptResult> {
  const res = await apiUpload<{ data: ReceiptResult }>(`/fleet/${kind}/${entryId}/receipt${queryString({ supplier: r.supplier.trim(), number: r.number.trim(), paid: r.paid })}`, new File([r.file!], r.file!.name, { type: r.file!.type || 'application/octet-stream' }));
  return res.data;
}

/** Ob der Benutzer Belege hochladen darf: Das Modul Eingangsrechnungen gehört dazu, und er darf Eingangsrechnungen anlegen */
export function useCanReceipt(): boolean {
  const { hasModule, can } = useSession();
  return hasModule('invoices_in') && can('invoices_in.write');
}

export function ReceiptFields({ value, onChange, supplierHint }: { value: ReceiptDraft; onChange: (next: ReceiptDraft) => void; supplierHint: string }) {
  const picker = useRef<HTMLInputElement>(null);
  const set = (patch: Partial<ReceiptDraft>) => onChange({ ...value, ...patch });
  return (
    <fieldset className="fleet-receipt">
      <legend>Beleg oder Rechnung (optional)</legend>
      <p className="muted small">Der Beleg geht direkt in die Eingangsrechnungen und zählt in den Finanzen als Aufwand. Das Sachkonto tragen Sie dort nachträglich ein.</p>
      <div className="form-grid">
        <div className="field">
          <span>Datei (PDF oder Foto)</span>
          <input ref={picker} type="file" accept="application/pdf,image/*" hidden onChange={(e) => { set({ file: e.target.files?.[0] ?? null }); e.target.value = ''; }} />
          <div className="fleet-receipt__file">
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => picker.current?.click()}>{value.file ? 'Andere Datei wählen' : 'Datei wählen'}</button>
            {value.file && <span className="small">{value.file.name} <button type="button" className="btn btn--ghost btn--sm" onClick={() => set({ file: null })} aria-label="Beleg entfernen">Entfernen</button></span>}
          </div>
        </div>
        {value.file && (
          <>
            <label className="field"><span>Lieferant</span><input className="input" value={value.supplier} onChange={(e) => set({ supplier: e.target.value })} maxLength={200} placeholder={supplierHint} required /></label>
            <label className="field"><span>Rechnungsnummer (optional)</span><input className="input" value={value.number} onChange={(e) => set({ number: e.target.value })} maxLength={100} placeholder="sonst vergibt Wirkia eine Belegnummer" /></label>
            <label className="field">
              <span>Bezahlt</span>
              <select className="input" value={value.paid} onChange={(e) => set({ paid: e.target.value as ReceiptDraft['paid'] })}>
                <option value="card">Ja, mit Karte</option>
                <option value="cash">Ja, bar</option>
                <option value="paypal">Ja, mit PayPal</option>
                <option value="bank_transfer">Ja, per Überweisung</option>
                <option value="">Nein, noch offen</option>
              </select>
            </label>
          </>
        )}
      </div>
    </fieldset>
  );
}
